import React, {
  EventHandler, memo, MouseEvent,
} from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';

import { getContrastColor } from 'app/lib/Color';

import Stamp from './elements/Stamp';
import Sender from './elements/Sender';

interface EnvelopeProps {
  className?: string,
  handleTapToOpen?: EventHandler<MouseEvent>,
  cardState: string,
  cardStatus: string,
  senderName: string,
  stampContainerRef?: React.MutableRefObject<null>,
  logoContainerRef?: React.MutableRefObject<null>,
  backgroundContainerRef?: React.MutableRefObject<null>,
  logo?: string,
  background?: string,
  colors: string[],
  isCustom: boolean,
}

const Envelope: React.FC<EnvelopeProps> = (props) => {
  const {
    className,
    handleTapToOpen,
    cardState,
    cardStatus,
    senderName,
    stampContainerRef,
    logoContainerRef,
    backgroundContainerRef,
    logo,
    background,
    colors,
    isCustom,
  } = props;

  const classes = useStyles();

  const senderColor = colors[2] || getContrastColor(colors[0], false, '#555');
  const tapColor = colors[4] || getContrastColor(colors[1], true, '#222');

  return (
    <div className={classnames(classes.container, className, { hideCovered: cardStatus === 'ready' })}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        className={classnames(classes.content, { [classes.pointer]: cardState === 'closed' })}
        onClick={cardState === 'closed' ? handleTapToOpen : undefined}
        style={{ backgroundColor: colors[0] }}
      >
        {backgroundContainerRef && (
          <div className={classes.background} ref={backgroundContainerRef} />
        )}
        {!backgroundContainerRef && (
          <div className={classes.background}>
            <img src={background} alt="" width="100%" />
          </div>
        )}
        <Stamp
          stampContainerRef={stampContainerRef}
          logoContainerRef={logoContainerRef}
          loaderColor={senderColor}
          logo={logo}
        />
        <Sender
          label="From:"
          text={senderName}
          colors={{
            label: colors[2],
            text: senderColor,
          }}
        />
        <div className={classes.footer}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
          <div
            className={classnames('tapToOpen', { [classes.pointer]: cardState === 'closed' })}
            onClick={cardState === 'opened' ? handleTapToOpen : undefined}
            style={{ backgroundColor: colors[1], color: tapColor }}
          >
            <span>Tap to Open</span>
          </div>
          <div className="copy" style={{ visibility: isCustom ? 'hidden' : 'visible' }}>Made with CardSnacks</div>
        </div>
      </div>
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    '&': {
      position: 'relative',
      display: 'flex',
      width: '32em',
      height: '48em',
      marginBottom: '-48em',
      background: '#fff',
      zIndex: '2',
      transition: '300ms opacity ease',
      opacity: '1',
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      top: '1.1em',
      right: '1.1em',
      bottom: '1.1em',
      left: '1.1em',
      opacity: '1',
      pointerEvents: 'none',
      background: '#f9f7f4',
      transition: '600ms opacity ease',
    },
    '&.hideCovered:after': {
      opacity: 0,
      pointerEvents: 'none',
    },
  },
  pointer: {
    pointerEvents: 'all',
  },
  hide: {
    opacity: 0,
    pointerEvents: 'none',
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    margin: '1.1em',
    background: '#f9f7f4',
  },
  background: {
    '&': {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      overflow: 'hidden',
    },
  },
  footer: {
    '&': {
      zIndex: 2,
      display: 'flex',
      textAlign: 'center',
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& .tapToOpen': {
      display: 'flex',
      backgroundColor: '#20B2A4',
      border: 0,
      fontFamily: '"Avenir Next Demi", sans-serif',
      fontSize: '1.4rem',
      outline: 'none',
      height: '4.4rem',
      borderRadius: '2.2rem',
      padding: '0 3.6rem',
      cursor: 'pointer',
      pointerEvents: 'all',
      alignItems: 'center',
      boxShadow: '0.1rem 0.2rem 0.5rem rgba(0, 0, 0, 0.15)',
    },
    '& .tapToOpen span': {
      display: 'block',
      marginTop: '0.133333333em',
    },
    '& .copy': {
      fontFamily: 'Avenir Next Demi, sans-serif',
      fontSize: '1.0rem',
      lineHeight: '5.7rem',
      fontWeight: 400,
      color: '#868686',
      marginTop: 0,
      marginBottom: '1.3rem',
    },
  },
});

export default memo(Envelope);
